import DoneIcon from "@mui/icons-material/Done";
import React, { useEffect, useState } from "react";
import FullHeight from "../../../shared/components/FullHeight";
import Loader from "../../../shared/components/Loader";
import Table from "../../../shared/components/tables/Table";
import SortTableHead, { TableColumnDefinition } from "../../../shared/components/SortTableHead";
import { urgencyStatus } from "../../myRfqs/models/models";
import { useAppDispatch, useAppSelector } from "../../../shared/hooks/sharedHooks";
import ActionToast from "../../../shared/components/ActionToast";
import TableEmpty from "../../../shared/components/tables/TableEmpty";
import BoxSearchIcon from "../../../shared/assets/icons/BoxSearchIcon";
import { Box, Button, Icon, TableBody, TableCell, TableRow, ThemeProvider, Toolbar, createTheme } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { NumericFormat } from "react-number-format";
import { productCondition } from "../../../common/models/CommonModels";
import Moment from "react-moment";
import WarningIcon from "../../../shared/assets/icons/WarningIcon";
import moment from "moment";
import TablePagination from "../../../shared/components/tables/TablePagination";
import { CustomerRfqFeedState, RfqsQueryParameter, RfqsQuery } from "../models/models";
import RfqFeedTableToolbar from "./RfqFeedTableToolbar";
import { changeQuery, getCategories, getRfqs, setError, setSuccess } from "../customerRfqFeedSlice";
import { NotificationMessage } from "../../../shared/models/models";
import customersApi from "../../shared/api/customersApi";
import { Customer, RoleCode } from "../../shared/models/customerModels";
import accountContext from "../../../account/accountContext";
import { refreshAccount } from "../../../account/accountSlice";
import { useConfirmation } from "../../../shared/components/confirmation/ConfirmationProvider";
import colors from "../../../shared/themes/colors";
import TickIcon from "../../../shared/assets/icons/TickIcon";
import useTranslation from "../../../shared/hooks/useTranslation";

export const RfqFeed = () => {
  const { query, rfqs, rfq, loading, categories, success, error } = useAppSelector<CustomerRfqFeedState>(
    (state: any) => state.customerRfqFeed
  );

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const confirm = useConfirmation();
  const { translate } = useTranslation("common");

  const columns: TableColumnDefinition[] = [
    { title: "Buyer company info" },
    { title: "Product details" },
    { title: "Condition" },
    { title: "Price/pc, quantity" },
    { title: "Urgency" },
    { title: "RFQ details", parameter: RfqsQueryParameter.created },
    { title: undefined }
  ];

  const goToCreateOffer = (id: string) => {
    if (id) {
      navigate(`/trading/new-offer/${id}`);
    }
  };

  const goToOffer = () => {
    navigate(`/trading/offers-sent`);
  };

  const onLogout = () => {
    accountContext.logout();
    dispatch(refreshAccount(null));
    navigate("/");
  };

  const handleQueryChange = (value: RfqsQuery) => {
    dispatch(changeQuery(value));
    dispatch(getRfqs(value));
  };

  const handleSort = (parameter: number) => {
    const sortDesc = query.sortBy !== parameter ? false : !query.sortDesc;
    const newQuery = { ...query, sortBy: parameter, sortDesc: sortDesc };

    dispatch(changeQuery(newQuery));
    dispatch(getRfqs(newQuery));
  };

  const [customer, setCustomer] = useState<Customer | undefined>();

  const loadCustomer = async () => {
    try {
      const customer = await customersApi.getCustomer();
      setCustomer(customer);
    } catch (error: any) {
      setError(error);
    }
  };

  const handlePageSizeChange = async (size: number) => {
    const newQuery = { ...query, pageNo: 1, pageSize: size };
    await dispatch(changeQuery(newQuery));
    dispatch(getRfqs(newQuery));
  };

  const handlePageNoChange = async (no: number) => {
    const newQuery = { ...query, pageNo: no };
    await dispatch(changeQuery(newQuery));
    dispatch(getRfqs(newQuery));
  };

  const formatLastCall = (lastCall: any) => {
    const timesBeforeLastCall = moment(lastCall).diff(moment());
    const hours = moment.duration(timesBeforeLastCall).hours();
    const minutes = moment.duration(timesBeforeLastCall).minutes();
    const timeToLastCall = `${hours}h${minutes}m`;
    return timeToLastCall;
  };

  const init = () => {
    dispatch(getRfqs(query));
    loadCustomer();
    dispatch(getCategories());
  };

  useEffect(() => {
    init();
  }, []);

  const theme = createTheme({
    components: {
      MuiToolbar: {
        styleOverrides: {
          root: {
            background: "#fff",
            borderBottom: "1px solid #d5d6d9"
          }
        }
      }
    }
  });

  return (
    <ThemeProvider theme={theme}>
      <ActionToast
        open={!!success}
        variant="success"
        message={success || ""}
        onClose={() => dispatch(setSuccess(undefined))}
        duration={6000}
      />

      <ActionToast
        open={!!error}
        variant="error"
        message={NotificationMessage.error(error).message || ""}
        onClose={() => dispatch(setError(undefined))}
        duration={6000}
      />

      <Toolbar className="flex items-center justify-between">
        <div className="flex items-center">
          <p className="heading-2">RFQ Feed</p>
        </div>
      </Toolbar>

      <Box className="px-4 v-fill">
        <RfqFeedTableToolbar
          className="mt-2"
          categories={categories}
          query={query as RfqsQuery}
          onChange={handleQueryChange}
        />

        {loading ? (
          <FullHeight verticalOffset="medium">
            <Loader />
          </FullHeight>
        ) : !rfqs?.items.length ? (
          <FullHeight verticalOffset="medium">
            <TableEmpty
              iconComponent={BoxSearchIcon}
              text="Empty RFQ Feed"
              secondaryText="Sorry, nothing is here"
            ></TableEmpty>
          </FullHeight>
        ) : (
          <Table>
            <SortTableHead columns={columns} sortBy={query.sortBy} sortDesc={query.sortDesc} onSort={handleSort} />

            <TableBody>
              {rfqs?.items.map((item) => (
                <TableRow key={item.id} selected={item.id === rfq?.id}>
                  <TableCell className="break-all" sx={{ maxWidth: "250px" }}>
                    <Box className="text-14-bold" color={"#333333"}>
                      {item.buyer.name}
                    </Box>
                  </TableCell>

                  <TableCell sx={{ maxWidth: "200px" }}>
                    <Box className="block break-all">
                      <Box>
                        <Box className="text-14-medium" color={"#333333"}>
                          {item.productNumber}
                        </Box>
                        <Box className="text-12-medium" color={"#747881"}>
                          {item.productAlternativeNumber}
                        </Box>
                      </Box>

                      <Box className="mt-2">
                        <Box className="text-12-medium" color={"#333333"}>
                          {item.manufacturer.name}
                        </Box>
                        <Box className="text-12-regular" color={"#747881"}>
                          {item.category.name}
                        </Box>
                      </Box>
                    </Box>
                  </TableCell>

                  <TableCell>
                    <Box className="text-14-medium" color={"#333333"}>
                      {item.productConditionCodes?.map((x) => translate(`productCondition.${productCondition(x)}`)).join(", ")}
                    </Box>
                  </TableCell>

                  <TableCell>
                    <Box className="text-18-medium" color={"#333333"}>
                      <NumericFormat
                        value={item.maxUnitPrice?.value}
                        displayType="text"
                        thousandSeparator=" "
                        suffix="€"
                      />
                    </Box>
                    <Box className="text-14-medium" color={"#333333"}>
                      {item.quantity + "\u00A0" + "pcs"}
                    </Box>
                  </TableCell>

                  <TableCell>
                    <Box className="text-14-medium" color={"#747881"}>
                      {urgencyStatus(item.urgencyCode)}
                    </Box>
                  </TableCell>

                  <TableCell sx={{ minWidth: "200px" }}>
                    <Box>
                      {moment().isBetween(moment(item.lastCall).subtract(1, "days"), moment(item.lastCall)) && (
                        <Box className="text-14-medium pb-1" color="#F23E3E">
                          RFQ closing in {formatLastCall(item.lastCall)}
                        </Box>
                      )}

                      <Box className="text-12-medium flex items-center" color={"#333333"}>
                        <Icon>
                          {moment().isBetween(moment(item.lastCall).subtract(1, "days"), moment(item.lastCall)) ? (
                            <WarningIcon color={colors.primary.red} />
                          ) : (
                            <WarningIcon />
                          )}
                        </Icon>

                        <Box className="ml-1">
                          Last call on{"\u00A0"}
                          <Moment date={item.lastCall} format="DD.MM.YYYY" />
                        </Box>
                      </Box>

                      <Box className="text-12-regular flex items-center mt-2" color={"#747881"}>
                        <Box className="ml-1">
                          Created on{"\u00A0"}
                          <Moment date={item.createdDate} format="DD.MM.YYYY" />
                        </Box>
                      </Box>
                    </Box>
                  </TableCell>

                  <TableCell sx={{ maxWidth: "150px" }}>
                    {customer?.roleCode === RoleCode.buyer ? (
                      <Button
                        variant="text"
                        className="text-14-medium text-left"
                        onClick={() =>
                          confirm({
                            title: "Now you will be logged out",
                            text: "In order to sign up as supplier, you will be logged out first. Do you want to continue?",
                            confirmButton: "Yes, log out",
                            closeButton: "No",
                            severity: "error",
                            onConfirm: () => onLogout()
                          })
                        }
                      >
                        Sign up as supplier to send offer
                      </Button>
                    ) : item.offerId ? (
                      <Box>
                        <Box className="text-14-medium flex items-center" color="#3EA293">
                          <Icon>
                            <TickIcon />
                          </Icon>
                          <Box>Offer sent</Box>
                        </Box>
                        <Button variant="text" onClick={goToOffer}>
                          To my offer
                        </Button>
                      </Box>
                    ) : (
                      <Button variant="text" onClick={() => goToCreateOffer(item.id)}>
                        Send an offer
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Box>

      <TablePagination
        variant="bottom"
        pageNo={query.pageNo}
        pageSize={query.pageSize}
        totalCount={rfqs?.totalCount}
        onPageNoChange={handlePageNoChange}
        onPageSizeChange={handlePageSizeChange}
      />
    </ThemeProvider>
  );
};
export default RfqFeed;
